import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowRight, CheckCircle2, Loader2, LogOut, XCircle } from 'lucide-react';
import { useAuthStore } from '@/hooks/useAuth';
import { getBilling, cancelProduct, formatMoney, type CustomerProduct } from '@/lib/billing-api';

/**
 * Kundenportal nach dem Umzug nach Publivo (Nico, 02.10.2026: „Zukünftig will ich das LMS hier überhaupt
 * nicht mehr für Kunden in irgendeiner Art und Weise benutzen.“).
 *
 * Erscheint statt des ganzen Portals, sobald customers.publivo_umgezogen_am gesetzt ist (App.tsx). Zeigt nur
 * noch den Weg zu Publivo und – solange hier noch ein Abo läuft – „zum Ende des bezahlten Monats kündigen“:
 * Die Kunden beenden ihr altes Abo selbst und aktivieren das neue in Publivo (Mail-Anleitung von Nico).
 */

const PUBLIVO_URL = 'https://dashboard.publivo.app';

function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Abos, die hier noch abgebucht werden (einmalige Käufe und Beendete zählen nicht). */
function laufendeAbos(products: CustomerProduct[]): CustomerProduct[] {
  return products.filter((p) => p.interval !== 'one_time' && (p.status === 'active' || p.status === 'past_due'));
}

export default function UmgezogenPage() {
  const { user, signOut } = useAuthStore();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['billing'], queryFn: () => getBilling() });
  const abos = laufendeAbos(data?.products ?? []);

  const kuendigen = useMutation({
    mutationFn: (id: string) => cancelProduct(id, 'cancel'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast.success('Gekündigt. Das Abo endet zum Ende des bezahlten Monats.');
    },
    onError: (err: Error) => toast.error(err.message || 'Die Kündigung hat nicht geklappt.'),
  });

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-xl">
          <h1 className="text-2xl font-bold text-gray-900">Dein Kundenbereich ist umgezogen</h1>
          <p className="text-gray-600 mt-3 leading-relaxed">
            {user?.customer?.name ? <>Alles für {user.customer.name} – </> : <>Alles – </>}
            Anfragen, Bewerbungen, die Zahlen zu deinen Seiten, der Monatsbericht und die Rechnungen – findest du
            jetzt an einem Ort in Publivo, im Dashboard und in der App. Hier musst du dich nicht mehr anmelden.
          </p>
          <a
            href={PUBLIVO_URL}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold"
          >
            Weiter zu Publivo <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {(isLoading || abos.length > 0) && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Dein altes Abo hier</h2>
            <p className="text-sm text-gray-500 mt-1">
              Bitte einmal kündigen – es läuft bis zum Ende des bezahlten Monats weiter. Das neue aktivierst du in
              Publivo unter Einstellungen → „Abo &amp; Rechnung“.
            </p>
            {isLoading ? (
              <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : (
              <ul className="mt-4 space-y-3">
                {abos.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 p-4">
                    <div>
                      <p className="font-semibold text-gray-900">{p.name}</p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {formatMoney(p.amount, p.currency)} im Monat
                        {p.current_period_end && (
                          <span className="ml-1">
                            · {p.cancel_at_period_end ? `endet am ${formatDate(p.current_period_end)}` : `bezahlt bis ${formatDate(p.current_period_end)}`}
                          </span>
                        )}
                      </p>
                    </div>
                    {p.cancel_at_period_end ? (
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700">
                        <CheckCircle2 className="w-4 h-4" /> Gekündigt
                      </span>
                    ) : (
                      <button
                        onClick={() => {
                          if (window.confirm(`„${p.name}“ kündigen? Es läuft bis zum Ende des bezahlten Monats weiter.`)) kuendigen.mutate(p.id);
                        }}
                        disabled={kuendigen.isPending}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-60"
                      >
                        {kuendigen.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Kündigen
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button onClick={() => { void signOut(); }} className="w-full inline-flex items-center justify-center gap-2 py-2 text-sm text-gray-500 hover:text-gray-700">
          <LogOut className="w-4 h-4" /> Abmelden
        </button>
      </div>
    </div>
  );
}
